<template>
  <div
    :data-ts="ts"
    ref="message"
    :class="[
      {
        '--comment': isCommentThread,
        '--attachment': isAttachment,
        '--feed': isFeed
      },
      'message'
    ]"
  >
    <template v-if="isMessageType(message)">
      <div
        v-if="isFirst && isFeed"
        data-role="message-user"
        class="message__user post-header"
      >
        <div class="d-flex align-items-center">
          <div class="placeholder-col">
            <subscriber-avatar :subscriber="user" small />
          </div>
          <div class="message__user--name">
            {{ username }}
            <div class="post-timestamp">
              {{ timestampAgo }}
            </div>
          </div>
        </div>
        <v-button
          class="mr-n1 mt-n1"
          round
          ghost
          secondary
          @click="handlePostClick"
        >
          <v-icon icon="more_vert" />
        </v-button>
      </div>
      <div
        v-else-if="isFirst"
        data-role="message-user"
        :class="[{ '--is-self': isSelf }, 'message__user']"
      >
        {{ username }}
      </div>
      <div
        :class="[
          {
            '--is-same': isSameAsPrev,
            '--is-last-by-user': isLast,
            '--is-first-by-user': isFirst,
            '--is-self': isSelf
          },
          'message__wrapper'
        ]"
      >
        <div class="placeholder-col">
          <subscriber-avatar
            v-if="!isFeed && isLast"
            :subscriber="user"
            x-small
          />
          <div v-if="isThread && isLast" class="thread__corner"></div>
        </div>
        <message-body
          :message-thread-id="messageThreadId"
          :message="message"
          :type="type"
          :is-self="isSelf"
          :is-feed="isFeed"
          @touchstart="e => handleMousedown(e, true)"
          @touchend="e => handleMouseup(e, true)"
          @mousedown="handleMousedown"
          @mouseup="handleMouseup"
          @showReactions="handleShowReactions"
          @selectImage="id => (this.selectingImageId = id)"
        />
      </div>

      <div
        v-if="isFeed ? isLast : true"
        :class="
          isFeed
            ? 'message__feed-footer d-flex justify-content-between align-items-center'
            : ''
        "
      >
        <reply-thread
          v-if="
            !hideReplies &&
              (!!message.reply_thread_id ||
                ((isCommentThread || isFeed) && !isThread && isLast))
          "
          :class="[{ '--is-self': isSelf }]"
          :is-feed="isFeed"
          :message="message"
        />

        <feed-like
          v-if="isFeed && isLast"
          :message="message"
          :is-liked="isLiked"
          @like="handleLike"
          @unlike="handleUnlike"
        />
      </div>
    </template>
    <template
      v-else-if="type === MESSAGE_TYPES.NOTIFICATION && !(isFeed || isThread)"
    >
      <div class="thread__notification text-center my-3">
        <small class="text-grey-60 font-weight-semi-bold">{{ body }}</small>
      </div>
    </template>
  </div>
</template>

<script>
import { mapGetters } from "vuex"

import { MESSAGE_TYPES, REACTION_TYPES } from "@/lib/pusher-utils"
import { timeAgo } from "@/lib/date-utils"
import { isPresent } from "@/lib/utils"
import { isIos, isStandalone } from "@/lib/is-platform"

import { generateImageKeyForMessage } from "@/lib/mk-lightbox"
import { isMessageType } from "@/lib/messages-utils"

import SubscriberAvatar from "@/components/SubscriberAvatar"
import MessageBody from "@/components/message-thread/MessageBody"

export default {
  components: {
    SubscriberAvatar,
    MessageBody,
    ReplyThread: () => import("@/components/message-thread/ReplyThread"),
    FeedLike: () => import("@/components/message-thread/FeedLike")
  },

  props: {
    message: { type: Object, required: true },
    messageThreadId: { type: [Number, String], required: true },
    isCommentThread: { type: Boolean, default: false },
    isFeed: { type: Boolean, default: false },
    isThread: { type: Boolean, default: false },
    isSelf: { type: Boolean, default: false },
    isSameAsPrev: { type: Boolean, default: false },
    isLast: { type: Boolean, default: false },
    isFirst: { type: Boolean, default: false },
    hideReplies: { type: Boolean, default: false }
  },

  data() {
    return {
      timestampAgo: null,
      interval: null,
      clickTimeoutId: null,
      longPressTimeout: null,
      isTouching: false,
      mouseDownStart: null,
      lastTouch: null,
      mouseDownY: null,
      elementDownY: null,
      selectingImageId: null,
      MESSAGE_TYPES,
      REACTION_TYPES
    }
  },

  computed: {
    ...mapGetters("messages", ["getParticipant", "getMessage"]),

    body() {
      return isPresent(this.message) ? this.message.message : ""
    },

    type() {
      return isPresent(this.message) ? this.message.message_type : null
    },

    ts() {
      return isPresent(this.message) ? this.message.message_ts : null
    },

    user() {
      return isPresent(this.message) ? this.message.user : null
    },

    username() {
      return isPresent(this.user)
        ? isPresent(this.user.name)
          ? this.user.name
          : this.user.email.split("@")[0]
        : null
    },

    isLiked() {
      return !!this.getParticipant(this.messageThreadId)?.liked_messages.find(
        item => item.message_ts === this.ts && item.user.id === this.user?.id
      )
    },

    isImage() {
      return this.type === MESSAGE_TYPES.IMAGE
    },

    isVideo() {
      return this.type === MESSAGE_TYPES.VIDEO
    },

    isAttachment() {
      return this.type === MESSAGE_TYPES.ATTACHMENT
    }
  },

  mounted() {
    this.updateTimestampAgo()

    if (this.isImage) {
      this.selectingImageId = generateImageKeyForMessage(this.ts)
    }

    this.interval = setInterval(() => {
      this.updateTimestampAgo()
    }, 60000)
  },

  beforeDestroy() {
    clearInterval(this.interval)
  },

  methods: {
    timeAgo,
    isMessageType,

    handleMousedown(event, fromTouch = false) {
      if (this.isFeed) return
      if (event.which === 3) return // Return right clicks
      if (this.isTouching && !fromTouch && !(isIos() && isStandalone())) return // Prefer touch events if both are present
      if (isIos() && isStandalone() && fromTouch) return // Prevents double events on iOS PWA

      this.isTouching = fromTouch
      this.mouseDownStart = new Date().getTime()
      this.mouseDownY = event.layerY
      this.elementDownY = event.target.getBoundingClientRect().y

      if (this.isTouching) {
        this.lastTouch = this.mouseDownStart
      }

      this.longPressTimeout = setTimeout(() => {
        // Prevents long press from triggering when scrolling
        if (
          Math.abs(event.target.getBoundingClientRect().y - this.elementDownY) >
            10 ||
          event.target.nodeName === "INPUT"
        )
          return

        this.$emit("showActions", this.message)
      }, 500)
    },

    handleMouseup(event, fromTouch = false) {
      const eventTime = new Date().getTime()
      const timeBetweenMouseDown = eventTime - this.mouseDownStart

      // We need to handle PWA iOS slightly differently, as the second mouseup event is fired
      // as a `mouseup` event not a `touchend` event. Apparently some webkit bug... PWA, Problems With Apple
      // So, instead we only respond to mouse events on iOS PWA's
      // https://stackoverflow.com/questions/74875307/javascript-touch-event-not-fired-when-clicking-repeatedly-in-standalone-pwa-on-i

      if (fromTouch && isIos() && isStandalone()) {
        // Reject due to likely false positive from iOS PWA bug
        return
      }

      if (this.isTouching && !fromTouch) {
        return
      }

      clearTimeout(this.longPressTimeout)

      this.isTouching = fromTouch

      // Return if the user has scrolled
      if (!this.clickTimeoutId && this.mouseDownY !== event.layerY) {
        return
      }

      // Return if the user press input (checkbox, range,...)
      if (event.target.nodeName === "INPUT") {
        return
      }

      const isLongPress = timeBetweenMouseDown > 250

      if (isLongPress) {
        this.$emit("showActions", this.message)
      } else {
        if (!this.clickTimeoutId) {
          this.clickTimeoutId = setTimeout(() => {
            this.clickTimeoutId = null

            if (event.target.nodeName === "IMG") {
              this.$emit("openImage", this.selectingImageId)
            } else if (!this.isVideo) {
              this.$emit("showActions", this.message)
            }
          }, 200)
        } else {
          clearTimeout(this.clickTimeoutId)
          this.clickTimeoutId = null

          if (!this.isLiked) {
            this.lastTouch = new Date().getTime()
            this.handleLike()
          }
        }
      }
    },

    handleLike() {
      this.handleReact({
        action: REACTION_TYPES.LIKE,
        message: this.message
      })
    },

    handleUnlike() {
      this.handleReact({
        action: REACTION_TYPES.UNLIKE,
        message: this.message
      })
    },

    handlePostClick() {
      const referingMessage = this.message.group_ts
        ? this.getMessage(this.messageThreadId, this.message.group_ts)
        : null

      if (
        referingMessage &&
        referingMessage.message_type === MESSAGE_TYPES.ATTACHMENT
      )
        this.$emit("showActions", referingMessage)
    },

    selectImage(e) {
      this.selectingImageId = e.target.id
    },

    handleReact(data) {
      this.$emit("react", data)
    },

    handleShowReactions() {
      this.$emit("showReactions", this.message)
    },

    updateTimestampAgo() {
      this.timestampAgo = this.timeAgo(this.ts)
    }
  }
}
</script>

<style lang="scss">
@import "@/assets/styles/variables";

$size--margin: calc(32px + 0.5rem);

.message {
  &.--comment {
    .message__wrapper {
      &.--is-first-by-user {
        margin-left: 0;
      }

      &.--is-self {
        flex-direction: row;

        .message__body {
          .body {
            border-top-right-radius: 16px;
            border-bottom-right-radius: 16px;
            border-top-left-radius: 4px;
            border-bottom-left-radius: 4px;
          }
        }

        &.--is-first-by-user {
          .message__body .body {
            border-top-right-radius: 16px;
            border-top-left-radius: 16px;
          }
        }

        &.--is-last-by-user {
          .message__body .body {
            border-bottom-right-radius: 16px;
            border-bottom-left-radius: 16px;
          }
        }

        .reaction-count {
          &.--at-end {
            right: 0;
            left: auto;
          }
        }
      }
    }

    .message__user {
      &.--is-self {
        text-align: left;
      }
    }
  }

  :not(.--comment) {
    .message__wrapper {
      .reaction-count {
        left: 50%;
        right: auto !important;

        &.--at-end {
          left: 0;
        }
      }
    }
  }

  &.--feed {
    .reply-thread {
      padding-top: $size--4;
      margin-left: 0;

      &.--is-self {
        .v-link {
          flex-direction: row;
        }
        .subscriber-avatars {
          margin-left: 0;
        }
      }
    }

    .message__user {
      margin-bottom: $size--2;
    }

    .message__wrapper {
      &.--is-self {
        flex-direction: row;

        .message__body {
          .body-text {
            background-color: $color-ui--grey-20;
            color: $color-ui--grey-90;
          }
        }
      }

      .placeholder-col {
        display: none;
      }

      .message__body {
        z-index: 1;
        position: relative;

        .body-text {
          margin-left: -$size--1;
        }
      }

      &.--is-last-by-user,
      &.--is-first-by-user {
        .message__body .body {
          border-top-left-radius: 4px;
          border-bottom-left-radius: 16px;
          border-bottom-right-radius: 16px;
        }
      }
    }

    &.--attachment {
      .message__wrapper {
        & > .p-relative {
          width: 100%;
        }

        &.--is-last-by-user:not(.--is-first-by-user) {
          margin-top: $size--3;
        }

        .message__body {
          .body-attachment {
            margin-top: $size--1;
            margin-left: -$size--5;
            border-radius: 0;
            width: calc(100% + 2rem);
            border-bottom: 1px solid $color-ui--grey-20;
          }

          .attachment-preview,
          .attachment-item.collection-item
            > .v-link
            > .collection-item__content {
            box-shadow: none;
            border-radius: 0;
          }
        }
      }
    }
  }
}

.message__user {
  margin: $size--4 $size--9 $size--1;
  font-size: $font-size--x-small;
  font-weight: $font-weight--semi-bold;
  color: $color-ui--grey-70;
  text-align: left;

  &.post-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin: 0;

    .placeholder-col {
      margin-right: $size--3;
    }

    .message__user--name {
      font-size: $font-size--small;
      font-weight: $font-weight--semi-bold;
      color: $color-ui--dark-blue;

      .post-timestamp {
        margin-top: 2px;
        font-size: $font-size--x-small;
        color: $color-ui--grey-70;
      }
    }
  }

  &.--is-self {
    text-align: right;
  }
}

.thread__corner {
  position: absolute;
  border: 2px solid $color-ui--grey-20;
  left: -20px;
  top: -$size--2;
  border-bottom-left-radius: 20px;
  border-right: none;
  border-top: none;
  height: $size--6;
  width: $size--4;
}

.message__wrapper {
  display: flex;
  flex-direction: row;
  align-items: flex-end;
  user-select: none;

  .subscriber-avatar {
    z-index: 5;
  }

  &.--is-self {
    flex-direction: row-reverse;

    .message__body {
      color: $color-ui--white;
      margin-right: $size--2;

      .body {
        border-top-left-radius: 16px;
        border-bottom-left-radius: 16px;
        border-top-right-radius: 4px;
        border-bottom-right-radius: 4px;
      }

      .body-text {
        color: var(--button-text-color-hex);
        background-color: var(--brand-color-hex);
      }

      .body-image {
        justify-content: flex-end;
      }
    }

    .reaction-count {
      &.--at-end {
        left: 0;
        right: auto;
      }
    }
  }

  &.--is-same {
    margin-bottom: 2px;
  }

  &.--is-first-by-user {
    .message__body .body {
      border-top-left-radius: 16px;
      border-top-right-radius: 16px;
    }
  }

  &.--is-last-by-user {
    margin-left: 0rem !important;
    margin-right: 0rem !important;

    .message__body .body {
      border-bottom-right-radius: 16px;
      border-bottom-left-radius: 16px;
    }
  }

  .placeholder-col {
    position: relative;
    min-width: 2rem;
    width: 2rem;
    min-width: 2rem;
    height: 2rem;
  }
}

.message__feed-footer {
  padding-top: $size--1;

  .reply-thread {
    max-width: 65%;
  }
}

@media screen and (max-width: $breakpoint--md) {
  .message__feed-footer {
    .reply-thread {
      margin-bottom: 0.2rem;
    }
  }
}
</style>
